import React, { useContext, useEffect, useState } from "react";
import {
  Form,
  Button,
  InlineStack,
  Page,
  Layout,
  Text,
  Icon,
} from "@shopify/polaris";
import { ArrowLeftIcon } from "@shopify/polaris-icons";

import useProductsStore from "../../store/product";
import { ToastContext } from "../../global-contexts/Toast";
import { useNavigate } from "react-router-dom";
import { ValidateBundle } from "../../validators/Bundles";
import { StoreDataContext } from "../../global-contexts/StoreData";
import BundleBasicDetails from "../../components/Bundles/BundleBasicDetails";
import BundleImages from "../../components/Bundles/BundleImages";
import SelectedProductsItems from "../../components/Bundles/SelectedProducts";
import AddProductModal from "../../components/Bundles/AddProductModal";
import PriceAndInventory from "../../components/Bundles/PriceAndInventory";
import { UploadFile, GeneratePresignedUrl } from "../../services/media";
import { GenerateText } from "../../services/ai/index";
import ProductOptions from "../../components/Bundles/ProductOptions";
import { GenerateCoverImage } from "../../services/bundle";

const BundleForm = ({
  bundle = null,
  editable = true,
  onSubmit,
  searchProduct,
  categories = [],
  boxes,
  isSearching,
  productPage,
  setProductPage,
  heading,
  form = "create",
}) => {
  const { store } = useContext(StoreDataContext);

  // form states
  const [name, setName] = useState(bundle?.name || "");
  const [description, setDescription] = useState(bundle?.description || "");
  const [selectedProducts, setSelectedProducts] = useState(
    bundle?.components || []
  );
  const { products } = useProductsStore();
  const [coverImage, setCoverImage] = useState(bundle?.coverImage || null);
  const [additionalImages, setAdditionalImages] = useState(
    bundle?.images || []
  );
  const [sku, setSku] = useState(bundle?.sku || "");
  const [price, setPrice] = useState(bundle?.price || "");
  const [onSale, setOnSale] = useState(bundle?.isOnSale || false);
  const [discount, setDiscount] = useState(bundle?.discount || "");
  const [status, setStatus] = useState(bundle?.status || "active");
  const [inventory, setInventory] = useState(bundle?.inventory || "");
  const [trackInventory, setTrackInventory] = useState(
    bundle?.trackInventory ?? true
  );

  const [showModal, setShowModal] = useState(false);
  const [tags, setTags] = useState(bundle?.tags || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newTag, setNewTag] = useState("");
  const [category, setCategory] = useState(
    bundle?.category || categories[0]?._id
  );
  const [vendor, setVendor] = useState(bundle?.vendor || store?.shopName || "");
  const [selectedBox, setSelectedBox] = useState(bundle?.box || "");
  const [compareAtPrice, setCompareAtPrice] = useState(
    bundle?.compareAtPrice || ""
  );

  const [selectedOptions, setSelectedOptions] = useState({});
  const { setToast } = useContext(ToastContext);
  const [aiGenerationState, setAiGenerationState] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [totalVariants, setTotalVariants] = useState(0);
  const navigate = useNavigate();

  const generateBundleOptions = () => {
    if (bundle && bundle?.options) {
      const newOptions = {};
      bundle.options.forEach((option) => {
        newOptions[option.product] = {
          options: option.options,
        };
      });
      setSelectedOptions({ ...newOptions });
    }
  };

  const handleProductSelection = ({ variantId, product }) => {
    setSelectedProducts((prevSelected) => {
      const exists = prevSelected.find(
        (item) => item.product._id === product._id
      );
      const imageUrls = product.images.map((img) => {
        return {
          url: img.src,
        };
      });

      if (exists) {
        setAdditionalImages(
          additionalImages.filter((additionalImage) => {
            const isImageSelected = product.images.find(
              (img) => additionalImage.url === img.src
            );
            if (!isImageSelected) {
              return additionalImage;
            } else {
              return false;
            }
          })
        );
        const newSelectedOptions = { ...selectedOptions };
        delete newSelectedOptions[product._id];
        setSelectedOptions(newSelectedOptions);
        return prevSelected.filter((item) => item.product._id !== product._id);
      } else {
        setAdditionalImages([...additionalImages, ...imageUrls]);
        return [
          ...prevSelected,
          {
            product,
            variantId: variantId,
            dimensions: {
              length: product.length ?? "",
              height: product.height ?? "",
              weight: product.weight ?? "",
              width: product.width ?? "",
            },
          },
        ];
      }
    });
  };

  const handleVariantChange = ({ productId, variantId }) => {
    const doesProductExists = selectedProducts.findIndex(
      (p) => p.product._id === productId
    );
    if (doesProductExists !== -1) {
      const newProducts = [...selectedProducts];
      newProducts[doesProductExists].variantId = variantId;
      setSelectedProducts(newProducts);
    }
  };

  const handleAddProducts = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    searchProduct("");
    setShowModal(false);
  };

  const handleDimensionChange = (productId, field, value) => {
    setSelectedProducts((prevSelected) =>
      prevSelected.map((item) => {
        if (item.product._id === productId) {
          return {
            ...item,
            dimensions: {
              ...item.dimensions,
              [field]: value,
            },
          };
        }
        return item;
      })
    );
  };

  const calculateBox = () => {
    if (boxes?.length) {
      let closestBox = null;
      let totalVolume = 0;
      selectedProducts.forEach((item) => {
        if (
          item.dimensions.length &&
          item.dimensions.width &&
          item.dimensions.height
        ) {
          totalVolume +=
            Number(item.dimensions.length) *
            Number(item.dimensions.width) *
            Number(item.dimensions.height);
        }
      });
      boxes.forEach((box) => {
        if (box.volume >= totalVolume) {
          if (!closestBox || box.volume < closestBox.volume) {
            closestBox = box;
          }
        }
      });
      setSelectedBox(closestBox?._id);
    }
  };

  const handleUploadImages = async ({ file }) => {
    if (file.url) {
      return;
    }
    const { data } = await GeneratePresignedUrl({
      fileName: file.name,
      fileType: file.type,
    });
    if (data.result) {
      const { presignedUrl, downloadUrl } = data.result;

      const res = await UploadFile({ file, url: presignedUrl });
      if (res.status === 200) {
        return downloadUrl;
      } else {
        throw new Error("Could not upload the file.");
      }
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const bundleDetails = {
      name,
      description,
      components: selectedProducts.map((p) => {
        return {
          productId: p.product._id,
          variantId: p.variantId,
          dimensions: p.dimensions,
        };
      }),
      price,
      isOnSale: onSale,
      discount: onSale ? discount : 0,
      status,
      coverImage: {},
      images: [],
      trackInventory,
      inventory,
      tags,
      category,
      box: selectedBox,
      vendor,
      sku,
      compareAtPrice,
      options: Object.entries(selectedOptions).map(([key, value]) => {
        return {
          product: key,
          options: value.options,
        };
      }),
    };
    if (totalVariants > 100) {
      setToast({
        error: true,
        message:
          "Your selected options can only generate 100 variants. Please adjust the options and option values and try again!",
        open: true,
      });
      setIsSubmitting(false);
      return;
    }
    const validationErrors = ValidateBundle({ bundle: bundleDetails });
    // return;

    if (validationErrors) {
      setToast({
        error: true,
        message: validationErrors.error,
        open: true,
      });
      setIsSubmitting(false);
      return;
    }
    let coverImageUrl = {};
    if (coverImage) {
      if (coverImage.url) {
        coverImageUrl = coverImage;
      } else {
        coverImageUrl = {
          url: await handleUploadImages({
            file: coverImage,
          }),
        };
      }
    }
    let imageUrls = [];
    if (additionalImages.length) {
      imageUrls = await Promise.all(
        additionalImages
          .map(async (img) => {
            if (img.url) {
              return img;
            } else {
              return { url: await handleUploadImages({ file: img }) };
            }
          })
          .filter(Boolean)
      );
    }
    bundleDetails.coverImage = coverImageUrl;
    bundleDetails.images = imageUrls;

    if (!bundleDetails.coverImage?.url) {
      setIsSubmitting(false);
      return setToast({
        error: true,
        message: "You need to provide with a cover image.",
        open: true,
      });
    }
    if (!bundleDetails.images || !bundleDetails.images.length) {
      setIsSubmitting(false);
      return setToast({
        error: true,
        message: "You need to select at least one image",
        open: true,
      });
    }
    try {
      const response = await onSubmit(bundleDetails);
      setToast({
        message: response.data.message,
        error: false,
        open: true,
      });
      setIsSubmitting(false);
      navigate("/bundles");
    } catch (e) {
      setToast({
        error: true,
        message: e.response?.data?.message || "Something went wrong",
        open: true,
      });
      setIsSubmitting(false);
    }
  };

  const handleCoverImageRemove = (e) => {
    e.stopPropagation();
    setCoverImage(null);
  };

  const handleAdditionalImagesDrop = (files) => {
    if (Array.isArray(files) && typeof files[0] === "string") {
      setAdditionalImages(files);
    } else {
      if (files.length + additionalImages.length > 5) {
        return alert("You can only upload up to 5 additional images.");
      }
      setAdditionalImages([...additionalImages, ...files]);
    }
  };

  const handleRemoveAdditionalImage = (index) => {
    const newImages = [...additionalImages];
    newImages.splice(index, 1);
    setAdditionalImages(newImages);
  };

  const handleCoverImageDrop = (files) => {
    if (typeof files === "string") {
      setCoverImage(files);
    } else {
      setCoverImage(files[0]);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };
  useEffect(() => {
    calculateBox();
  }, [selectedProducts]);

  const generateTitle = async () => {
    setAiGenerationState("Generating Title...");
    if (!selectedProducts.length) {
      throw new Error("No selected bundles");
    }
    const prompt = `These are ${
      selectedProducts.length
    } products belonging to a single bundle :- ${selectedProducts
      .map((p) => sanitizeInput(p.product.title))
      .join(
        ";"
      )}. Generate a single and short title for the bundle of these products`;
    const { data } = await GenerateText(prompt);
    if (data.result) {
      setName(data.result);
      generateDescription(data.result);
    }
  };
  const sanitizeInput = (text) => {
    return text.replace(/[\r\n\t]+/g, " ").replace(/["'\\]/g, "");
  };
  const generateDescription = async (generatedName) => {
    setAiGenerationState("Generating Description...");

    if (!selectedProducts.length) {
      throw new Error("No selected bundles");
    }
    const sanitizedName = sanitizeInput(generatedName);
    const sanitizedDescriptions = selectedProducts.map((p) =>
      sanitizeInput(p.product.description)
    );

    const prompt = `
The title of the bundle is ${sanitizedName}.
It includes ${
      sanitizedDescriptions.length
    } products with the following descriptions:
${sanitizedDescriptions
  .map((desc, index) => `${index + 1}. ${desc}`)
  .join("\n")}
Please combine these descriptions into a single, detailed, and cohesive description for the bundle.

  `;

    try {
      const { data } = await GenerateText(prompt);
      if (data?.result) {
        setDescription(data.result);
        generateTags(data.result);
      } else {
        throw new Error("Failed to generate description");
      }
    } catch (error) {
      throw error;
    }
  };
  const generateTags = async (generatedDes) => {
    setAiGenerationState("Generating Tags...");

    const prompt = `The following is a product description: '${sanitizeInput(
      generatedDes
    )}' Generate a list of relevant tags based on this description. The tags should be concise, meaningful, and separated by commas.`;

    try {
      const { data } = await GenerateText(prompt);

      if (data?.result) {
        const tags = data.result.split(",");
        setTags(tags);
      } else {
        throw new Error("Failed to generate description");
      }
    } catch (e) {
      throw e;
    }
  };
  const aiGenerateContent = async () => {
    try {
      if (!selectedProducts.length) {
        throw new Error("No selected products");
      }
      setIsGenerating(true);

      await Promise.all([generateTitle(), generateCoverImageHandler()]);
      setIsGenerating(false);
      setToast({
        error: false,
        message: "Content generation completed successfully",
        open: true,
      });
    } catch (e) {
      setToast({
        error: true,
        message: e.message || "something went wrong",
        open: true,
      });
    }
  };

  useEffect(() => {
    generateBundleOptions();
  }, [bundle]);

  useEffect(() => {
    calculateTotalVariants();
  }, [selectedOptions]);

  const calculateTotalVariants = () => {
    let totalVariants = 1;
    Object.values(selectedOptions).forEach((productOption) => {
      productOption.options.forEach((option) => {
        totalVariants *= option?.values?.length || 0;
      });
    });
    setTotalVariants(totalVariants);
  };

  const handleOptionSelection = ({ productId, option }) => {
    setSelectedOptions((prev) => {
      const updatedOptions = { ...prev };

      const isProductOptionSelected = updatedOptions[productId];
      if (isProductOptionSelected) {
        const isOptionSelected = isProductOptionSelected.options.find(
          (selectedOption) => selectedOption.name === option.name
        );

        if (isOptionSelected) {
          updatedOptions[productId].options =
            isProductOptionSelected.options.filter(
              (selectedOption) => selectedOption.name !== option.name
            );

          if (updatedOptions[productId].options.length === 0) {
            delete updatedOptions[productId];
          }
        } else {
          const totalSelectedOptions = Object.values(prev).reduce(
            (total, product) => total + product.options.length,
            0
          );

          // If adding this option exceeds the limit of 3, log a message and return the current state
          if (totalSelectedOptions >= 3) {
            setToast({
              error: true,
              message: "You can only select up to 3 options in total.",
              open: true,
            });
            return prev;
          }
          updatedOptions[productId].options = [
            ...isProductOptionSelected.options,
            option,
          ];
        }
      } else {
        updatedOptions[productId] = {
          options: [option],
        };
      }

      return updatedOptions;
    });
  };
  const handleVariantRemove = ({ product, optionName, optionValue }) => {
    // Deep clone to avoid modifying the original state
    const newState = structuredClone(selectedOptions);

    const selectedOption = newState[product]?.options?.find((option) => {
      return option.name === optionName;
    });

    if (selectedOption) {
      const valueIndex = selectedOption.values.findIndex(
        (v) => v === optionValue
      );
      if (valueIndex !== -1) {
        // Remove the value
        selectedOption.values = selectedOption.values.filter(
          (value) => value !== optionValue
        );
      } else {
        selectedOption.values.push(optionValue);
      }
    }

    setSelectedOptions(newState);
  };

  const generateCoverImageHandler = async () => {
    try {
      const productIds = selectedProducts.map((product) => product.product._id);
      console.log(productIds);
      const { data } = await GenerateCoverImage({
        productIds,
      });

      if (data.result) {
        setCoverImage({
          url: data.result,
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Page
      title={
        <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
          <div onClick={() => navigate("/bundles")}>
            <Icon tone="primary" source={ArrowLeftIcon} />
          </div>

          <Text as="h1" variant="headingLg">
            {heading}
          </Text>
        </div>
      }
    >
      <Form implicitSubmit={false} noValidate={false} onSubmit={handleSubmit}>
        <Layout>
          <Layout.Section>
            <Text as="h3" variant="headingSm">
              Add Products In Bundle
            </Text>
            <SelectedProductsItems
              disabled={form === "update"}
              boxes={boxes}
              selectedBox={selectedBox}
              handleAddProducts={handleAddProducts}
              handleDimensionChange={handleDimensionChange}
              handleProductSelection={handleProductSelection}
              handleVariantChange={handleVariantChange}
              selectedProducts={selectedProducts}
              setSelectedBox={setSelectedBox}
              key={"bundle-selected-products"}
            />
            <br />

            {selectedProducts?.length ? (
              <>
                <Text as="h3" variant="headingSm">
                  Select product options (upto 3). Total generated Variants
                  allowed 100. Currently selected {totalVariants}
                </Text>
                <ProductOptions
                  disabled={form === "update"}
                  handleOptionChange={handleOptionSelection}
                  selectedProductOptions={selectedOptions}
                  handleOptionValueRemove={handleVariantRemove}
                  productOptions={selectedProducts.map((s) => {
                    return {
                      productName: s.product.title,
                      options: s.product.options,
                      productId: s.product._id,
                    };
                    // s.options;
                  })}
                />
              </>
            ) : (
              ""
            )}
          </Layout.Section>
          <Layout.Section>
            <InlineStack align="space-between">
              <Text as="h3" variant="headingSm">
                Basic Details
              </Text>
              <Button disabled={isGenerating} onClick={aiGenerateContent}>
                {isGenerating ? (
                  aiGenerationState
                ) : (
                  <div
                    style={{
                      display: "flex",
                      maxWidth: "120px",
                      justifyContent: "space-between",
                    }}
                  >
                    <Text>Generate with AI</Text>
                    <img
                      src={`${process.env.PUBLIC_URL}/magic_wand.svg`}
                      alt="Magic Wand Icon"
                      style={{
                        verticalAlign: "middle",
                        width: "10%",
                      }}
                    />
                  </div>
                )}
              </Button>
            </InlineStack>

            <BundleBasicDetails
              categories={categories}
              category={category}
              description={description}
              editable={editable}
              handleAddTag={handleAddTag}
              handleRemoveTag={handleRemoveTag}
              name={name}
              newTag={newTag}
              setCategory={setCategory}
              setDescription={setDescription}
              setName={setName}
              setNewTag={setNewTag}
              setSku={setSku}
              setStatus={setStatus}
              setVendor={setVendor}
              sku={sku}
              status={status}
              tags={tags}
              vendor={vendor}
              key={"bundle-basic-details"}
            />
          </Layout.Section>

          <Layout.Section>
            <Text as="h3" variant="headingSm">
              Bundle Images
            </Text>
            <BundleImages
              additionalImages={additionalImages}
              coverImage={coverImage}
              editable={editable}
              handleAdditionalImagesDrop={handleAdditionalImagesDrop}
              handleCoverImageDrop={handleCoverImageDrop}
              handleCoverImageRemove={handleCoverImageRemove}
              handleRemoveAdditionalImage={handleRemoveAdditionalImage}
              key={"bundle-images-section"}
            />
          </Layout.Section>

          <AddProductModal
            handleCloseModal={handleCloseModal}
            handleProductSelection={handleProductSelection}
            isSearching={isSearching}
            productPage={productPage}
            products={products}
            searchProduct={searchProduct}
            selectedProducts={selectedProducts}
            setProductPage={setProductPage}
            showModal={showModal}
            key={"bundle-add-product-modal"}
          />
          <Layout.Section>
            <Text as="h3" variant="headingSm">
              Pricing
            </Text>
            {/* <br /> */}
            <PriceAndInventory
              editable={editable}
              inventory={inventory}
              discount={discount}
              onSale={onSale}
              price={price}
              setDiscount={setDiscount}
              setInventory={setInventory}
              setOnSale={setOnSale}
              setPrice={setPrice}
              setTrackInventory={setTrackInventory}
              trackInventory={trackInventory}
              key={"bundle-price-inventory"}
              compareAtPrice={compareAtPrice}
              setCompareAtPrice={setCompareAtPrice}
            />
          </Layout.Section>
          <Layout.Section>
            <InlineStack align="start">
              <Button
                disabled={!editable}
                loading={isSubmitting}
                onClick={() => {
                  handleSubmit();
                }}
                size="large"
                variant="primary"
              >
                Submit
              </Button>
            </InlineStack>
          </Layout.Section>
        </Layout>
      </Form>
    </Page>
  );
};

export default BundleForm;
